import { useMutation } from "@tanstack/react-query";

import { CollectionRecord, CollectionResourceRef } from "../CollectionRecord";

import { useUpdateCollection } from "./useUpdateCollection";

interface AddToCollectionsVariables {
  collectionIds: string[];
  resourceOwnerId: string;
  resources: CollectionResourceRef[];
}

export interface AddToCollectionsResult {
  succeeded: CollectionRecord[];
  failed: Array<{ collectionId: string; error: Error }>;
}

/**
 * Add the same resources to several collections at once.
 *
 * Purely an orchestrator over {@link useUpdateCollection}: the API is
 * per-collection, so this fans out N writes and partitions the outcomes for a
 * picker that must report partial success. It performs no cache maintenance of
 * its own -- each inner `mutateAsync` invalidates before it resolves, which is
 * what keeps this path and every other collection write in agreement.
 *
 * The cost of that, accepted knowingly: N writes fire N invalidations over
 * largely the same keys, and `refetchQueries` defaults to `cancelRefetch:
 * true`, so the repeats cancel and restart the same refetch. That is inherent
 * to a per-collection API; the fix is narrower keys, not a batching seam that
 * would put cache policy back outside the write.
 */
export function useAddToCollections() {
  const { mutateAsync: updateCollection } = useUpdateCollection();

  return useMutation<AddToCollectionsResult, Error, AddToCollectionsVariables>({
    mutationFn: async ({ collectionIds, resourceOwnerId, resources }) => {
      const settled = await Promise.allSettled(
        collectionIds.map((collectionId) =>
          updateCollection({
            collectionId,
            resourceOwnerId,
            updates: { add_resources: resources },
          }),
        ),
      );

      const succeeded: CollectionRecord[] = [];
      const failed: Array<{ collectionId: string; error: Error }> = [];

      settled.forEach((r, i) => {
        if (r.status === "fulfilled") {
          succeeded.push(r.value);
        } else {
          failed.push({
            collectionId: collectionIds[i],
            error:
              r.reason instanceof Error
                ? r.reason
                : new Error(String(r.reason)),
          });
        }
      });

      if (succeeded.length === 0) {
        throw failed[0]?.error ?? new Error("Failed to add to any collection");
      }

      return { succeeded, failed };
    },
  });
}
